import React, { useState, useEffect, useRef } from 'react';
import { VolumeX, Play, Pause, Settings, RotateCcw } from 'lucide-react';

interface TextToSpeechProps {
  text: string;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSpeedChange: (speed: number) => void;
  onVoiceChange: (voice: SpeechSynthesisVoice) => void;
  className?: string;
}

interface TTSState {
  isSupported: boolean;
  isPlaying: boolean;
  isPaused: boolean;
  currentWordIndex: number;
  totalWords: number;
  currentTime: number;
  duration: number;
  speed: number;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  volume: number;
  pitch: number;
}

const TextToSpeech: React.FC<TextToSpeechProps> = ({
  text,
  isPlaying,
  onPlay,
  onPause,
  onStop,
  onSpeedChange,
  onVoiceChange,
  className = ''
}) => {
  const [state, setState] = useState<TTSState>({
    isSupported: false,
    isPlaying: false,
    isPaused: false,
    currentWordIndex: 0,
    totalWords: 0,
    currentTime: 0,
    duration: 0,
    speed: 1,
    voices: [],
    selectedVoice: null,
    volume: 1,
    pitch: 1
  });

  const [showSettings, setShowSettings] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize TTS
  useEffect(() => {
    const initTTS = () => {
      if ('speechSynthesis' in window) {
        setState(prev => ({
          ...prev,
          isSupported: true,
          voices: speechSynthesis.getVoices(),
          totalWords: text.split(' ').length
        }));

        // Load voices when they become available
        const loadVoices = () => {
          const voices = speechSynthesis.getVoices();
          setState(prev => ({
            ...prev,
            voices,
            selectedVoice: voices.find(v => v.default) || voices[0] || null
          }));
        };

        if (speechSynthesis.getVoices().length > 0) {
          loadVoices();
        } else {
          speechSynthesis.addEventListener('voiceschanged', loadVoices);
        }
      }
    };

    initTTS();

    return () => {
      if (utteranceRef.current) {
        speechSynthesis.cancel();
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [text]);

  // Handle play/pause
  useEffect(() => {
    if (isPlaying && !state.isPlaying) {
      startSpeaking();
    } else if (!isPlaying && state.isPlaying) {
      pauseSpeaking();
    }
  }, [isPlaying, state.isPlaying]);

  const startSpeaking = () => {
    if (!state.isSupported) return;

    // Cancel any existing speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = state.speed;
    utterance.volume = state.volume;
    utterance.pitch = state.pitch;
    
    if (state.selectedVoice) {
      utterance.voice = state.selectedVoice;
    }

    // Event handlers
    utterance.onstart = () => {
      setState(prev => ({ ...prev, isPlaying: true, isPaused: false }));
      onPlay();
      startProgressTracking();
    };

    utterance.onend = () => {
      setState(prev => ({ 
        ...prev, 
        isPlaying: false, 
        isPaused: false, 
        currentWordIndex: 0,
        currentTime: 0
      }));
      onStop();
      stopProgressTracking();
    };

    utterance.onerror = (event) => {
      console.error('TTS Error:', event.error);
      setState(prev => ({ ...prev, isPlaying: false, isPaused: false }));
      onStop();
      stopProgressTracking();
    };

    utterance.onpause = () => {
      setState(prev => ({ ...prev, isPaused: true }));
      onPause();
    };

    utterance.onresume = () => {
      setState(prev => ({ ...prev, isPaused: false }));
      onPlay();
    };

    utteranceRef.current = utterance;
    speechSynthesis.speak(utterance);
  };

  const pauseSpeaking = () => {
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause();
    } else if (speechSynthesis.paused) {
      speechSynthesis.resume();
    }
  };

  const stopSpeaking = () => {
    speechSynthesis.cancel();
    setState(prev => ({ 
      ...prev, 
      isPlaying: false, 
      isPaused: false, 
      currentWordIndex: 0,
      currentTime: 0
    }));
    onStop();
    stopProgressTracking();
  };

  const startProgressTracking = () => {
    progressIntervalRef.current = setInterval(() => {
      if (utteranceRef.current && speechSynthesis.speaking) {
        const elapsed = (Date.now() - (utteranceRef.current as any).startTime) / 1000;
        const progress = Math.min(elapsed / (state.duration || 1), 1);
        const wordIndex = Math.floor(progress * state.totalWords);
        
        setState(prev => ({
          ...prev,
          currentTime: elapsed,
          currentWordIndex: wordIndex
        }));
      }
    }, 100);
  };

  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleSpeedChange = (newSpeed: number) => {
    setState(prev => ({ ...prev, speed: newSpeed }));
    onSpeedChange(newSpeed);
    
    if (utteranceRef.current) {
      utteranceRef.current.rate = newSpeed;
    }
  };

  const handleVoiceChange = (voice: SpeechSynthesisVoice) => {
    setState(prev => ({ ...prev, selectedVoice: voice }));
    onVoiceChange(voice);
    
    if (utteranceRef.current) {
      utteranceRef.current.voice = voice;
    }
  };

  const handleVolumeChange = (volume: number) => {
    setState(prev => ({ ...prev, volume }));
    
    if (utteranceRef.current) {
      utteranceRef.current.volume = volume;
    }
  };

  const handlePitchChange = (pitch: number) => {
    setState(prev => ({ ...prev, pitch }));
    
    if (utteranceRef.current) {
      utteranceRef.current.pitch = pitch;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!state.isSupported) {
    return (
      <div className={`text-center p-4 text-gray-500 ${className}`}>
        <VolumeX className="w-8 h-8 mx-auto mb-2" />
        <p>Text-to-speech is not supported in this browser</p>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      {/* Main Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={state.isPlaying ? pauseSpeaking : startSpeaking}
            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            disabled={!text.trim()}
          >
            {state.isPlaying ? (
              state.isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5" />
            )}
          </button>

          <button
            onClick={stopSpeaking}
            className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
            disabled={!state.isPlaying && !state.isPaused}
          >
            <RotateCcw className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>{formatTime(state.currentTime)}</span>
            <span>/</span>
            <span>{formatTime(state.duration || 0)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {state.currentWordIndex} / {state.totalWords} words
          </div>
          
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${state.totalWords > 0 ? (state.currentWordIndex / state.totalWords) * 100 : 0}%` 
            }}
          />
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
          {/* Speed Control */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Speed: {state.speed}x
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={state.speed}
              onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Volume Control */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Volume: {Math.round(state.volume * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={state.volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Pitch Control */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Pitch: {state.pitch}x
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={state.pitch}
              onChange={(e) => handlePitchChange(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Voice Selection */}
          {state.voices.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">Voice</label>
              <select
                value={state.selectedVoice?.name || ''}
                onChange={(e) => {
                  const voice = state.voices.find(v => v.name === e.target.value);
                  if (voice) handleVoiceChange(voice);
                }}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              >
                {state.voices.map((voice, index) => (
                  <option key={index} value={voice.name}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TextToSpeech;
